// React component

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FaGoogle, FaEye, FaEyeSlash, FaSpinner, FaCheck } from 'react-icons/fa';
import { motion } from 'framer-motion';
import logo from '../assets/logo.png';
import EmbeddedBrowserModal from '../components/EmbeddedBrowserModal';
import { isEmbeddedBrowser } from '../lib/firebase';

const Register = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showEmbeddedBrowserModal, setShowEmbeddedBrowserModal] = useState(false);
  const [isInEmbeddedBrowser, setIsInEmbeddedBrowser] = useState(false);
  
  const { register, loginWithGoogle, currentUser, authLoading, loading } = useAuth();
  const navigate = useNavigate();

  // Check for embedded browser on mount - fresh check every time, no caching
  useEffect(() => {
    // Force fresh detection - clear any potential cached state
    const checkEmbeddedBrowser = () => {
      const detected = isEmbeddedBrowser();
      setIsInEmbeddedBrowser(detected);
      
      // Auto-show modal if in embedded browser
      if (detected) {
        console.log('[Register] Embedded browser detected - showing modal');
        setShowEmbeddedBrowserModal(true);
      }
    };
    
    // Check immediately
    checkEmbeddedBrowser();
    
    // Also check when window regains focus (in case user switches back)
    window.addEventListener('focus', checkEmbeddedBrowser);
    
    return () => {
      window.removeEventListener('focus', checkEmbeddedBrowser);
    };
  }, []);

  // Password strength checker
  useEffect(() => {
    const password = formData.password;
    let strength = 0;
    
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    setPasswordStrength(strength);
  }, [formData.password]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    const { firstName, lastName, email, password, confirmPassword } = formData;
    
    if (!firstName.trim()) {
      toast.error('First name is required'); // Keep as is for now since it's validation
      return false;
    }
    
    if (!lastName.trim()) {
      toast.error('Last name is required');
      return false;
    }
    
    if (!email) {
      toast.error(t('errors.fillAllFields'));
      return false;
    }
    
    if (password.length < 8) {
      toast.error(t('errors.passwordTooShort'));
      return false;
    }
    
    if (password !== confirmPassword) {
      toast.error(t('errors.passwordsDontMatch'));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const { firstName, lastName, email, password } = formData;
      const displayName = `${firstName} ${lastName}`;
      
      await register(email, password, {
        displayName,
        firstName,
        lastName
      });
      
      // Navigate to verify email page after successful registration
      navigate('/verify-email');
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = t('errors.registrationFailed');
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = t('errors.emailInUse');
          break;
        case 'auth/invalid-email':
          errorMessage = t('errors.invalidEmail');
          break;
        case 'auth/weak-password':
          errorMessage = t('errors.weakPassword');
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/password accounts are not enabled';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    // Always do a fresh check for embedded browser - no caching
    const isEmbedded = isEmbeddedBrowser();
    setIsInEmbeddedBrowser(isEmbedded);
    
    if (isEmbedded) {
      console.log('[Register] Google Sign-In blocked - embedded browser detected');
      setShowEmbeddedBrowserModal(true);
      return;
    }
    
    try {
      await loginWithGoogle();
      // Note: The redirect will handle the success message and navigation
    } catch (error) {
      console.error('Google sign-in error:', error);
      
      // Show modal for embedded browser error (fallback)
      if (error.code === 'auth/embedded-browser') {
        setIsInEmbeddedBrowser(true);
        setShowEmbeddedBrowserModal(true);
        return;
      }
      
      let errorMessage = t('errors.googleSignInFailed');
      
      switch (error.code) {
        case 'auth/popup-blocked':
          errorMessage = t('errors.popupBlocked');
          break;
        case 'auth/popup-closed-by-user':
          errorMessage = t('errors.popupClosed');
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = t('errors.accountExistsDifferent');
          break;
        default:
          errorMessage = error.message || errorMessage;
      }
      
      toast.error(errorMessage);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'bg-red-500';
      case 2:
        return 'bg-orange-500';
      case 3:
        return 'bg-yellow-500';
      case 4:
        return 'bg-blue-500';
      case 5:
        return 'bg-green-500';
      default:
        return 'bg-gray-300';
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'Very Weak';
      case 2:
        return 'Weak';
      case 3:
        return 'Fair';
      case 4:
        return 'Good';
      case 5:
        return 'Strong';
      default:
        return '';
    }
  };

  return (
    <div dir="ltr" className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-white to-[#EFF6FF] flex relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-20 w-72 h-72 bg-[#2F80ED]/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-96 h-96 bg-[#60A5FA]/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Left Side - Branding */}
      <motion.div 
        className="hidden lg:flex lg:w-1/2 bg-white relative overflow-hidden"
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute inset-0 opacity-[0.03]"
            animate={{
              backgroundPosition: ['0px 0px', '80px 80px'],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, #2563EB 1px, transparent 1px)',
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        {/* Floating animated dots */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-[#2563EB] rounded-full"
            style={{
              left: `${15 + (i * 12)}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + (i * 0.5),
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.2,
            }}
          />
        ))}

        {/* Larger floating points */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={`large-${i}`}
            className="absolute w-3 h-3 bg-[#2563EB]/30 rounded-full blur-[1px]"
            style={{
              right: `${10 + (i * 15)}%`,
              bottom: `${15 + (i % 4) * 20}%`,
            }}
            animate={{
              y: [0, 40, 0],
              opacity: [0.1, 0.4, 0.1],
              scale: [1, 2, 1],
            }}
            transition={{
              duration: 4 + (i * 0.3),
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3,
            }}
          />
        ))}

        {/* Animated Arrow/Divider - Points to the form */}
        <motion.div 
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20"
          animate={{
            x: [0, -10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="relative">
            {/* Arrow shape */}
            <div className="w-0 h-0 border-t-[30px] border-t-transparent border-l-[40px] border-l-[#2563EB] border-b-[30px] border-b-transparent"></div>
            {/* Glow effect */}
            <motion.div
              className="absolute top-1/2 left-0 -translate-y-1/2 w-12 h-12 bg-[#2563EB]/20 rounded-full blur-xl -z-10"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>
        </motion.div>

        {/* Vertical decorative line with animation */}
        <motion.div 
          className="absolute right-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-[#2563EB]/30 to-transparent"
          animate={{
            opacity: [0.3, 0.8, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <div className="relative z-10 flex flex-col justify-center px-16 text-[#1E293B]">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.2, type: "spring" }}
          >
            <Link to="/" className="inline-block mb-8 cursor-pointer group">
              <motion.img 
                src={logo} 
                alt="CVLogs Logo" 
                className="w-32 h-32 object-contain"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </Link>
          </motion.div>
          
          <motion.h1 
            className="text-5xl font-bold mb-6 pb-2 bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {i18n.language === 'tr' ? 'Yolculuğunuza Başlayın' : isRTL ? 'ابدأ رحلتك' : 'Start Your Journey'}
          </motion.h1>
          
          <motion.p 
            className="text-xl text-[#64748B] leading-relaxed mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {i18n.language === 'tr' ? "Başarılı özgeçmişler oluşturmak için CVLogs'a güvenen binlerce profesyonele katılın." : isRTL ? "انضم إلى آلاف المحترفين الذين يثقون في CVLogs لإنشاء سير ذاتية احترافية." : "Join thousands of professionals who trust CVLogs to create winning resumes."}
          </motion.p>
          
          <div className="space-y-6">
            {[
              { icon: "✓", text: i18n.language === 'tr' ? 'Ücretsiz Hesap Kurulumu' : isRTL ? 'إنشاء حساب مجاني' : 'Free Account Setup', delay: 0.5 },
              { icon: "✓", text: i18n.language === 'tr' ? 'Profesyonel Şablonlar' : isRTL ? 'قوالب احترافية' : 'Professional Templates', delay: 0.6 },
              { icon: "✓", text: i18n.language === 'tr' ? 'Yapay Zeka İçerik Önerileri' : isRTL ? 'اقتراحات محتوى بالذكاء الاصطناعي' : 'AI Content Suggestions', delay: 0.7 }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="flex items-center space-x-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: item.delay }}
                whileHover={{ x: 10 }}
              >
                <motion.div 
                  className="w-12 h-12 rounded-full bg-[#2563EB]/10 backdrop-blur-sm flex items-center justify-center border-2 border-[#2563EB]/20"
                  whileHover={{ 
                    scale: 1.2, 
                    backgroundColor: "rgba(37, 99, 235, 0.2)",
                    borderColor: "rgba(37, 99, 235, 0.4)",
                    boxShadow: "0 0 20px rgba(37, 99, 235, 0.3)"
                  }}
                >
                  <span className="text-2xl text-[#2563EB]">{item.icon}</span>
                </motion.div>
                <span className="text-lg font-medium text-[#1E293B]">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-16 py-12 relative z-10">
        <motion.div 
          className="w-full max-w-md"
          dir={isRTL ? 'rtl' : 'ltr'}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {/* Mobile Logo */}
          <motion.div 
            className="lg:hidden text-center mb-8"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            <Link to="/" className="inline-block">
              <motion.img 
                src={logo} 
                alt="CVLogs Logo" 
                className="w-24 h-24 mx-auto mb-4 object-contain"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div 
            className="text-center lg:text-left mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-4xl font-bold text-[#1E293B] mb-2 text-center">
              {t('auth.getStarted')}
            </h2>
            <p className="text-[#64748B] text-lg text-center">
              {t('auth.createAccount')}
            </p>
          </motion.div>

          {/* Registration Form */}
          <motion.div 
            className="bg-white/80 backdrop-blur-xl p-8 rounded-2xl shadow-xl border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <form className="space-y-5" onSubmit={handleSubmit}>
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold mb-2 text-[#1E293B]">
                    {t('auth.firstName')}
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2F80ED] transition-colors text-[#1E293B]"
                    placeholder={t('auth.firstNamePlaceholder', 'First name')}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold mb-2 text-[#1E293B]">
                    {t('auth.lastName', 'Last Name')}
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2F80ED] transition-colors text-[#1E293B]"
                    placeholder={t('auth.lastNamePlaceholder', 'Last name')}
                  />
                </div>
              </div>

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold mb-2 text-[#1E293B]">
                  {t('auth.emailAddress')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2F80ED] transition-colors text-[#1E293B]"
                  placeholder={t('auth.enterEmail')}
                />
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold mb-2 text-[#1E293B]">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2F80ED] transition-colors text-[#1E293B] pr-12"
                    placeholder={t('auth.enterPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#64748B] hover:text-[#2F80ED] transition-colors"
                  >
                    {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="mt-3">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-[#E2E8F0] rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                          style={{ width: `${(passwordStrength / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-[#64748B]">
                        {getPasswordStrengthText()}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Input */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold mb-2 text-[#1E293B]">
                  {t('auth.confirmPassword')}
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2F80ED] transition-colors text-[#1E293B] pr-12"
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#64748B] hover:text-[#2F80ED] transition-colors"
                  >
                    {showConfirmPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                  </button>
                </div>
                
                {/* Password Match Indicator */}
                {formData.confirmPassword && (
                  <div className="mt-2 flex items-center space-x-2">
                    {formData.password === formData.confirmPassword ? (
                      <>
                        <FaCheck className="text-green-500 text-sm" />
                        <span className="text-green-500 text-sm font-medium">Passwords match</span>
                      </>
                    ) : (
                      <span className="text-red-500 text-sm font-medium">{t('errors.passwordsDontMatch')}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Sign Up Button */}
              <motion.button
                type="submit"
                disabled={isLoading || authLoading}
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white font-semibold py-3.5 px-4 rounded-xl hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-[#2563EB]/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading || authLoading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    {t('auth.creatingAccount')}
                  </>
                ) : (
                  t('auth.createAccountBtn')
                )}
              </motion.button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E2E8F0]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-[#64748B] font-medium">{t('auth.orContinueWith')}</span>
                </div>
              </div>

              {/* Google Sign In Button */}
              <motion.button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={authLoading}
                className={`w-full font-semibold py-3.5 px-4 rounded-xl focus:outline-none focus:ring-4 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center ${
                  isInEmbeddedBrowser 
                    ? 'bg-amber-50 border-2 border-amber-300 text-amber-800 hover:bg-amber-100 focus:ring-amber-200' 
                    : 'bg-white border-2 border-[#E2E8F0] text-[#1E293B] hover:bg-[#F8FAFC] hover:border-[#2563EB] hover:shadow-md focus:ring-[#E2E8F0]'
                }`}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {authLoading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    {t('auth.connectingGoogle')}
                  </>
                ) : (
                  <>
                    <FaGoogle className={`mr-3 text-lg ${isInEmbeddedBrowser ? 'text-amber-600' : 'text-[#DB4437]'}`} />
                    {isInEmbeddedBrowser ? t('embeddedBrowser.googleNotAvailable', 'Google Sign-Up (Open in Browser)') : t('auth.signUpGoogle')}
                  </>
                )}
              </motion.button>
            </form>

            {/* Sign In Link */}
            <motion.div 
              className="text-center pt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <p className="text-[#64748B]">
                {t('auth.hasAccount')}{' '}
                <Link
                  to="/login"
                  className="font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                >
                  {t('auth.signInNow')}
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Embedded Browser Modal */}
      <EmbeddedBrowserModal 
        isOpen={showEmbeddedBrowserModal} 
        onClose={() => setShowEmbeddedBrowserModal(false)} 
      />
    </div>
  );
};

export default Register;
